import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, inject } from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import { BehaviorSubject, combineLatest, mergeMap, Observable, Subject } from 'rxjs'
import { LocationFilter } from '../../location-filter'
import { StorageNodeService } from '../storage-node.service'
import { StorageNode, StorageNodeAssignment } from '../storage-node'
import { filter, map, takeUntil } from 'rxjs/operators'
import { StoragePath } from '../../storage-path'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { PackageBrowserComponent } from '../../packages/package-browser/package-browser.component'
import { FormsModule } from '@angular/forms'
import { MatInput } from '@angular/material/input'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { AsyncPipe } from '@angular/common'
import { MatSlideToggle } from '@angular/material/slide-toggle'
import { MatIcon } from '@angular/material/icon'
import { MatIconButton } from '@angular/material/button'
import {
  ElderToolbarColumnDirective,
  ElderOverlayOriginDirective,
  ElderOverlayTriggerDirective,
  ElderOverlayComponent,
  ElderCardPanelComponent,
  ElderFormFieldLabelDirective,
  ElderQuantityPipe,
  ElderBasicPaneLayoutComponent,
  ElderPaneComponent,
  ElderPaneContentComponent,
} from '@elderbyte/ngx-starter'

@Component({
  selector: 'app-storage-node-assignment',
  templateUrl: './storage-node-assignment.component.html',
  styleUrls: ['./storage-node-assignment.component.scss'],
  imports: [
    ElderToolbarColumnDirective,
    MatIconButton,
    ElderOverlayOriginDirective,
    ElderOverlayTriggerDirective,
    MatIcon,
    ElderOverlayComponent,
    ElderCardPanelComponent,
    MatSlideToggle,
    MatFormField,
    ElderFormFieldLabelDirective,
    MatLabel,
    MatInput,
    FormsModule,
    PackageBrowserComponent,
    AsyncPipe,
    ElderTranslatePipe,
    ElderQuantityPipe,
    ElderBasicPaneLayoutComponent,
    ElderPaneComponent,
  ],
})
export class StorageNodeAssignmentComponent implements OnInit, OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _destroy$ = new Subject<void>()

  public readonly node$ = new BehaviorSubject<StorageNode>(null)
  public readonly deepSearch$ = new BehaviorSubject<boolean>(true)

  public readonly nodeAssignment$: Observable<StorageNodeAssignment>
  public readonly locationFilter$: Observable<LocationFilter>

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const nodeService = inject(StorageNodeService)
    this.locationFilter$ = combineLatest([this.node$, this.deepSearch$]).pipe(
      takeUntil(this._destroy$),
      filter(([node, deepSearch]) => !!node),
      map(([node, deepSearch]) => new LocationFilter(StoragePath.parse(node.path), deepSearch))
    )

    this.nodeAssignment$ = combineLatest([this.node$, this.deepSearch$]).pipe(
      takeUntil(this._destroy$),
      filter(([node, deepSearch]) => !!node),
      mergeMap(([node, deepSearch]) => nodeService.loadAssignment(node.id, deepSearch))
    )
  }

  /***************************************************************************
   *                                                                         *
   * Lifecycle                                                               *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {}

  public ngOnDestroy(): void {
    this._destroy$.next()
    this._destroy$.complete()
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set node(node: StorageNode) {
    this.node$.next(node)
  }
}
